import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Edit2, X, Play, ChevronDown, ChevronRight, BookOpen, BarChart2, CheckCircle, Eye } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../utils/toast';

const novoId = () => Date.now() + Math.random();

// ── Extrai ID do YouTube ─────────────────────────────────────────────────
function getYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&\n?#]+)/);
  return m ? m[1] : null;
}

function EmbedVideo({ url, titulo }) {
  const ytId = getYoutubeId(url);
  if (ytId) return (
    <div style={{ maxWidth: 480, width: '100%' }}>
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        <iframe
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0&modestbranding=1`}
          title={titulo}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
  if (url) return (
    <div style={{ color: '#f59e0b', fontSize: 13 }}>⚠️ URL inválida. Use um link do YouTube (ex: https://www.youtube.com/watch?v=...)</div>
  );
  return null;
}

// ── Componente principal ─────────────────────────────────────────────────
export function Trilhas({ trilhas = [], setTrilhas, usuarioAtual, isAdmin, usuarios = [], turmas = [], cursos = [] }) {
  const [aba, setAba]                   = useState(isAdmin ? 'gerenciar' : 'trilhas');
  const [trilhaSel, setTrilhaSel]       = useState(null);
  const [assuntoSel, setAssuntoSel]     = useState(null);
  const [videoSel, setVideoSel]         = useState(null);
  const [visualizacoes, setVisualizacoes] = useState([]);
  const [formTrilha, setFormTrilha]     = useState(null);
  const [formAssunto, setFormAssunto]   = useState(null);
  const [formVideo, setFormVideo]       = useState(null);
  const [assuntosAbertos, setAssuntosAbertos] = useState({});
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroTrilha, setFiltroTrilha]  = useState('');

  const userId = usuarioAtual?.id;

  // Carrega visualizações do Supabase
  const carregarVisualizacoes = async () => {
    const query = supabase.from('visualizacoes').select('*');
    const { data } = isAdmin ? await query : await query.eq('user_id', userId);
    setVisualizacoes(data || []);
  };

  useEffect(() => { carregarVisualizacoes(); }, [userId]);
  useEffect(() => { if (aba === 'relatorio') carregarVisualizacoes(); }, [aba]);

  // Marca vídeo como assistido
  const marcarAssistido = async (trilhaId, assuntoId, videoId) => {
    const jaViu = visualizacoes.some(v => v.user_id === userId && v.video_id === String(videoId));
    if (jaViu) return;
    const { error } = await supabase.from('visualizacoes').insert({
      user_id:    userId,
      trilha_id:  String(trilhaId),
      assunto_id: String(assuntoId),
      video_id:   String(videoId),
      empresa:    usuarioAtual?.empresa || '',
      nome_usuario: usuarioAtual?.nome || '',
    });
    if (!error) {
      setVisualizacoes(prev => [...prev, { user_id: userId, video_id: String(videoId), trilha_id: String(trilhaId), assunto_id: String(assuntoId), created_at: new Date().toISOString(), empresa: usuarioAtual?.empresa || '', nome_usuario: usuarioAtual?.nome || '' }]);
      toast.sucesso('Vídeo marcado como assistido!');
    }
  };

  const jaAssistiu = (videoId) => visualizacoes.some(v => v.user_id === userId && v.video_id === String(videoId));

  // ── CRUD Trilhas ──────────────────────────────────────────────────────
  // Trilhas visíveis para o usuário atual (admin vê todas; aluno só vê as que tem acesso)
  const trilhasVisiveis = useMemo(() => {
    if (isAdmin) return trilhas;
    // Acesso por usuário marcado OU por estar matriculado numa turma liberada.
    const minhasTurmas = new Set(turmas.filter(tu => (tu.alunosIds || []).includes(String(userId))).map(tu => String(tu.id)));
    return trilhas.filter(t =>
      (t.usuariosPermitidos || []).includes(userId) ||
      (t.turmasPermitidas || []).some(id => minhasTurmas.has(String(id)))
    );
  }, [trilhas, isAdmin, userId, turmas]);

  const salvarTrilha = () => {
    if (!formTrilha?.nome) return;
    if (formTrilha.id) {
      setTrilhas(trilhas.map(t => t.id === formTrilha.id ? { ...t, nome: formTrilha.nome, descricao: formTrilha.descricao, usuariosPermitidos: formTrilha.usuariosPermitidos || [], turmasPermitidas: formTrilha.turmasPermitidas || [] } : t));
      toast.sucesso('Trilha atualizada!');
    } else {
      setTrilhas([...trilhas, { id: novoId(), nome: formTrilha.nome, descricao: formTrilha.descricao || '', assuntos: [], usuariosPermitidos: formTrilha.usuariosPermitidos || [], turmasPermitidas: formTrilha.turmasPermitidas || [] }]);
      toast.sucesso('Trilha criada!');
    }
    setFormTrilha(null);
  };

  const excluirTrilha = (id) => {
    if (!window.confirm('Excluir esta trilha e todos os seus assuntos e vídeos?')) return;
    setTrilhas(trilhas.filter(t => t.id !== id));
    toast.sucesso('Trilha excluída.');
  };

  // ── CRUD Assuntos ─────────────────────────────────────────────────────
  const salvarAssunto = () => {
    if (!formAssunto?.nome || !formAssunto?.trilhaId) return;
    setTrilhas(trilhas.map(t => {
      if (t.id !== formAssunto.trilhaId) return t;
      if (formAssunto.id) {
        return { ...t, assuntos: t.assuntos.map(a => a.id === formAssunto.id ? { ...a, nome: formAssunto.nome, descricao: formAssunto.descricao } : a) };
      }
      return { ...t, assuntos: [...(t.assuntos || []), { id: novoId(), nome: formAssunto.nome, descricao: formAssunto.descricao || '', videos: [] }] };
    }));
    setFormAssunto(null);
    toast.sucesso('Assunto salvo!');
  };

  const excluirAssunto = (trilhaId, assuntoId) => {
    if (!window.confirm('Excluir este assunto e seus vídeos?')) return;
    setTrilhas(trilhas.map(t => t.id !== trilhaId ? t : { ...t, assuntos: t.assuntos.filter(a => a.id !== assuntoId) }));
  };

  // ── CRUD Vídeos ───────────────────────────────────────────────────────
  const salvarVideo = () => {
    if (!formVideo?.titulo || !formVideo?.url) return;
    setTrilhas(trilhas.map(t => {
      if (t.id !== formVideo.trilhaId) return t;
      return {
        ...t, assuntos: t.assuntos.map(a => {
          if (a.id !== formVideo.assuntoId) return a;
          if (formVideo.id) {
            return { ...a, videos: a.videos.map(v => v.id === formVideo.id ? { ...v, titulo: formVideo.titulo, url: formVideo.url, descricao: formVideo.descricao } : v) };
          }
          return { ...a, videos: [...(a.videos || []), { id: novoId(), titulo: formVideo.titulo, url: formVideo.url, descricao: formVideo.descricao || '' }] };
        })
      };
    }));
    setFormVideo(null);
    toast.sucesso('Vídeo salvo!');
  };

  const excluirVideo = (trilhaId, assuntoId, videoId) => {
    setTrilhas(trilhas.map(t => {
      if (t.id !== trilhaId) return t;
      return { ...t, assuntos: t.assuntos.map(a => a.id !== assuntoId ? a : { ...a, videos: a.videos.filter(v => v.id !== videoId) }) };
    }));
  };

  // ── Relatório ─────────────────────────────────────────────────────────
  const empresas = useMemo(() => [...new Set(usuarios.filter(u => u.empresa).map(u => u.empresa))].sort(), [usuarios]);

  const relatorio = useMemo(() => {
    let vis = visualizacoes;
    if (filtroEmpresa) vis = vis.filter(v => v.empresa === filtroEmpresa);
    if (filtroTrilha)  vis = vis.filter(v => v.trilha_id === String(filtroTrilha));

    // Agrupa por usuário
    const porUsuario = {};
    vis.forEach(v => {
      if (!porUsuario[v.user_id]) {
        const u = usuarios.find(u => u.id === v.user_id);
        porUsuario[v.user_id] = { nome: v.nome_usuario || u?.nome || v.user_id, empresa: v.empresa || u?.empresa || '—', videos: [] };
      }
      // Encontra título do vídeo
      let tituloVideo = v.video_id, tituloTrilha = v.trilha_id, tituloAssunto = v.assunto_id;
      trilhas.forEach(t => {
        if (String(t.id) === v.trilha_id) {
          tituloTrilha = t.nome;
          (t.assuntos || []).forEach(a => {
            if (String(a.id) === v.assunto_id) {
              tituloAssunto = a.nome;
              (a.videos || []).forEach(vi => { if (String(vi.id) === v.video_id) tituloVideo = vi.titulo; });
            }
          });
        }
      });
      porUsuario[v.user_id].videos.push({ trilha: tituloTrilha, assunto: tituloAssunto, video: tituloVideo, data: v.created_at });
    });
    return Object.values(porUsuario);
  }, [visualizacoes, filtroEmpresa, filtroTrilha, trilhas, usuarios]);

  const exportarRelatorio = () => {
    const linhas = ['Aluno;Empresa;Trilha;Assunto;Vídeo;Data'];
    relatorio.forEach(r => r.videos.forEach(v => {
      linhas.push(`"${r.nome}";"${r.empresa}";"${v.trilha}";"${v.assunto}";"${v.video}";"${v.data ? new Date(v.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}"`);
    }));
    const blob = new Blob([linhas.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'relatorio_trilhas.csv'; a.click();
  };

  // ── Renderização ──────────────────────────────────────────────────────
  const ABAS = isAdmin
    ? [{ id: 'trilhas', label: '🎯 Trilhas' }, { id: 'gerenciar', label: '⚙️ Gerenciar' }, { id: 'relatorio', label: '📊 Relatório' }]
    : [{ id: 'trilhas', label: '🎯 Minhas Trilhas' }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Abas */}
      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e5e0ef', paddingBottom: '0.75rem', flexWrap: 'wrap' }}>
        {ABAS.map(a => (
          <button key={a.id} onClick={() => setAba(a.id)}
            style={{ background: aba === a.id ? '#7c3aed' : 'transparent', border: `1px solid ${aba === a.id ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 20, color: aba === a.id ? 'white' : '#5f5874', padding: '7px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            {a.label}
          </button>
        ))}
      </div>

      {/* ── ABA: TRILHAS (alunos e admins) ── */}
      {aba === 'trilhas' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {trilhasVisiveis.length === 0 && <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Nenhuma trilha disponível.</p>}
          {trilhasVisiveis.map(trilha => (
            <div key={trilha.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => setTrilhaSel(trilhaSel?.id === trilha.id ? null : trilha)}>
                <div>
                  <div style={{ color: '#1c1530', fontWeight: 700, fontSize: 15 }}>🎯 {trilha.nome}</div>
                  {trilha.descricao && <div style={{ color: '#5f5874', fontSize: 12, marginTop: 2 }}>{trilha.descricao}</div>}
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
                    {(trilha.assuntos || []).length} assunto(s) · {(trilha.assuntos || []).reduce((s, a) => s + (a.videos || []).length, 0)} vídeo(s)
                  </div>
                </div>
                {trilhaSel?.id === trilha.id ? <ChevronDown size={16} color="#5f5874"/> : <ChevronRight size={16} color="#5f5874"/>}
              </div>

              {trilhaSel?.id === trilha.id && (
                <div style={{ borderTop: '1px solid #eeeaf5', padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {(trilha.assuntos || []).length === 0 && <p style={{ color: '#64748b', fontSize: 13 }}>Nenhum assunto nesta trilha.</p>}
                  {(trilha.assuntos || []).map(assunto => (
                    <div key={assunto.id} style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onClick={() => setAssuntosAbertos(p => ({ ...p, [assunto.id]: !p[assunto.id] }))}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <BookOpen size={14} color="#7c3aed"/>
                          <span style={{ color: '#2d2640', fontWeight: 600, fontSize: 13 }}>{assunto.nome}</span>
                          <span style={{ fontSize: 11, color: '#64748b' }}>{(assunto.videos || []).length} vídeo(s)</span>
                        </div>
                        {assuntosAbertos[assunto.id] ? <ChevronDown size={14} color="#5f5874"/> : <ChevronRight size={14} color="#5f5874"/>}
                      </div>

                      {assuntosAbertos[assunto.id] && (
                        <div style={{ borderTop: '1px solid #eeeaf5', padding: '0.75rem' }}>
                          {(assunto.videos || []).length === 0 && <p style={{ color: '#64748b', fontSize: 12, padding: '0.5rem' }}>Nenhum vídeo neste assunto.</p>}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {(assunto.videos || []).map(video => {
                              const assistiu = jaAssistiu(video.id);
                              return (
                                <div key={video.id} style={{ border: `1px solid ${assistiu ? 'rgba(16,185,129,0.3)' : '#e5e0ef'}`, borderRadius: 8, overflow: 'hidden', background: assistiu ? 'rgba(16,185,129,0.03)' : 'transparent' }}>
                                  <div style={{ padding: '0.625rem 0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      {assistiu ? <CheckCircle size={14} color="#10b981"/> : <Play size={14} color="#5f5874"/>}
                                      <span style={{ color: '#1c1530', fontSize: 13, fontWeight: 500 }}>{video.titulo}</span>
                                    </div>
                                    <button
                                      onClick={() => setVideoSel(videoSel?.id === video.id ? null : { ...video, trilhaId: trilha.id, assuntoId: assunto.id })}
                                      style={{ background: '#7c3aed', border: 'none', borderRadius: 6, color: 'white', padding: '5px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                      <Play size={12}/> {videoSel?.id === video.id ? 'Fechar' : 'Assistir'}
                                    </button>
                                  </div>

                                  {videoSel?.id === video.id && (
                                    <div style={{ padding: '0.75rem', borderTop: '1px solid #eeeaf5', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                      <EmbedVideo url={video.url} titulo={video.titulo}/>
                                      {video.descricao && <p style={{ color: '#5f5874', fontSize: 12, margin: 0 }}>{video.descricao}</p>}
                                      {!assistiu && (
                                        <button onClick={() => marcarAssistido(trilha.id, assunto.id, video.id)}
                                          style={{ alignSelf: 'flex-start', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, color: '#10b981', padding: '6px 14px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                          <CheckCircle size={13}/> Marcar como assistido
                                        </button>
                                      )}
                                      {assistiu && <span style={{ fontSize: 12, color: '#10b981' }}>✓ Você já assistiu este vídeo</span>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── ABA: GERENCIAR (admin) ── */}
      {aba === 'gerenciar' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#1c1530', fontWeight: 600, fontSize: 15 }}>Gerenciar Trilhas</span>
            <button onClick={() => setFormTrilha({ nome: '', descricao: '', usuariosPermitidos: [] })}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Plus size={14}/> Nova Trilha
            </button>
          </div>

          {/* Form Nova/Editar Trilha */}
          {formTrilha && (
            <div style={sc.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={sc.cardTitulo}>{formTrilha.id ? 'Editar Trilha' : 'Nova Trilha'}</span>
                <button onClick={() => setFormTrilha(null)} style={sc.btnX}><X size={15}/></button>
              </div>
              <input style={sc.input} placeholder="Nome da trilha *" value={formTrilha.nome} onChange={e => setFormTrilha({ ...formTrilha, nome: e.target.value })}/>
              <input style={sc.input} placeholder="Descrição (opcional)" value={formTrilha.descricao} onChange={e => setFormTrilha({ ...formTrilha, descricao: e.target.value })}/>
              {/* Turmas com acesso: todo aluno matriculado na turma vê a trilha */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: '#5f5874', fontSize: 12, fontWeight: 600 }}>Turmas com acesso <span style={{ fontWeight: 400 }}>(todos os alunos da turma veem a trilha)</span></span>
                <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '0.5rem', maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {turmas.length === 0 && <span style={{ color: '#64748b', fontSize: 12 }}>Nenhuma turma cadastrada. Crie em Treinamentos → Cursos &amp; Turmas.</span>}
                  {[...turmas].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR')).map(tu => {
                    const ids = (formTrilha.turmasPermitidas || []).map(String);
                    const sel = ids.includes(String(tu.id));
                    const curso = cursos.find(c => String(c.id) === String(tu.cursoId));
                    return (
                      <label key={tu.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, background: sel ? 'rgba(124,58,237,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={sel} style={{ accentColor: '#7c3aed' }}
                          onChange={() => setFormTrilha(prev => ({ ...prev, turmasPermitidas: sel ? ids.filter(i => i !== String(tu.id)) : [...ids, String(tu.id)] }))} />
                        <span style={{ color: '#2d2640', fontSize: 13 }}>{tu.nome}</span>
                        {curso && <span style={{ color: '#64748b', fontSize: 11 }}>({curso.nome} · {(tu.alunosIds || []).length} aluno(s))</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              {/* Usuários com acesso */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ color: '#5f5874', fontSize: 12, fontWeight: 600 }}>Usuários com acesso <span style={{ fontWeight: 400 }}>(além das turmas)</span></span>
                <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '0.5rem', maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {usuarios.length === 0 && <span style={{ color: '#64748b', fontSize: 12 }}>Nenhum usuário cadastrado</span>}
                  {[...usuarios].sort((a, b) => (a.nome || a.email || '').localeCompare(b.nome || b.email || '', 'pt-BR')).map(u => {
                    const selecionado = (formTrilha.usuariosPermitidos || []).includes(u.id);
                    return (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '2px 4px', borderRadius: 4, background: selecionado ? 'rgba(124,58,237,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={selecionado}
                          onChange={() => setFormTrilha(prev => ({
                            ...prev,
                            usuariosPermitidos: selecionado
                              ? (prev.usuariosPermitidos || []).filter(id => id !== u.id)
                              : [...(prev.usuariosPermitidos || []), u.id]
                          }))}
                          style={{ accentColor: '#7c3aed' }}
                        />
                        <span style={{ color: '#2d2640', fontSize: 13 }}>{u.nome || u.email}</span>
                        {u.empresa && <span style={{ color: '#64748b', fontSize: 11 }}>({u.empresa})</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={salvarTrilha} style={sc.btnSalvar}>Salvar</button>
                <button onClick={() => setFormTrilha(null)} style={sc.btnCancelar}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Lista de trilhas */}
          {trilhas.map(trilha => (
            <div key={trilha.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, overflow: 'hidden' }}>
              {/* Cabeçalho da trilha */}
              <div style={{ padding: '0.875rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f7f5fb' }}>
                <div>
                  <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 14 }}>🎯 {trilha.nome}</span>
                  {trilha.descricao && <span style={{ color: '#5f5874', fontSize: 12, marginLeft: 8 }}>{trilha.descricao}</span>}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setFormAssunto({ nome: '', descricao: '', trilhaId: trilha.id })}
                    style={{ ...sc.btnIcone, color: '#10b981' }} title="Novo assunto"><Plus size={14}/></button>
                  <button onClick={() => setFormTrilha({ id: trilha.id, nome: trilha.nome, descricao: trilha.descricao || '', usuariosPermitidos: trilha.usuariosPermitidos || [], turmasPermitidas: trilha.turmasPermitidas || [] })}
                    style={sc.btnIcone}><Edit2 size={13}/></button>
                  <button onClick={() => excluirTrilha(trilha.id)}
                    style={{ ...sc.btnIcone, color: '#ef4444' }}><Trash2 size={13}/></button>
                </div>
              </div>

              {/* Form Assunto */}
              {formAssunto?.trilhaId === trilha.id && (
                <div style={{ ...sc.card, margin: '0.75rem', borderColor: 'rgba(16,185,129,0.3)' }}>
                  <span style={sc.cardTitulo}>{formAssunto.id ? 'Editar Assunto' : 'Novo Assunto'}</span>
                  <input style={sc.input} placeholder="Nome do assunto *" value={formAssunto.nome} onChange={e => setFormAssunto({ ...formAssunto, nome: e.target.value })}/>
                  <input style={sc.input} placeholder="Descrição (opcional)" value={formAssunto.descricao} onChange={e => setFormAssunto({ ...formAssunto, descricao: e.target.value })}/>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={salvarAssunto} style={sc.btnSalvar}>Salvar</button>
                    <button onClick={() => setFormAssunto(null)} style={sc.btnCancelar}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* Assuntos */}
              <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(trilha.assuntos || []).length === 0 && <p style={{ color: '#64748b', fontSize: 12, padding: '0.25rem 0' }}>Nenhum assunto ainda. Clique em + para adicionar.</p>}
                {(trilha.assuntos || []).map(assunto => (
                  <div key={assunto.id} style={{ background: '#ffffff', border: '1px solid #eeeaf5', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Cabeçalho do assunto */}
                    <div style={{ padding: '0.625rem 0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(124,58,237,0.06)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <BookOpen size={13} color="#7c3aed"/>
                        <span style={{ color: '#2d2640', fontSize: 13, fontWeight: 600 }}>{assunto.nome}</span>
                        <span style={{ color: '#64748b', fontSize: 11 }}>{(assunto.videos || []).length} vídeo(s)</span>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => setFormAssunto({ id: assunto.id, nome: assunto.nome, descricao: assunto.descricao || '', trilhaId: trilha.id })}
                          style={sc.btnIcone} title="Editar assunto"><Edit2 size={12}/></button>
                        <button onClick={() => excluirAssunto(trilha.id, assunto.id)}
                          style={{ ...sc.btnIcone, color: '#ef4444' }} title="Excluir assunto"><Trash2 size={12}/></button>
                      </div>
                    </div>

                    {/* Lista de vídeos */}
                    <div style={{ padding: '0.625rem 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {(assunto.videos || []).length === 0 && (
                        <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>Nenhum vídeo neste assunto.</p>
                      )}
                      {(assunto.videos || []).map(video => (
                        <div key={video.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', background: '#ffffff', borderRadius: 6, border: '1px solid #eeeaf5' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Play size={12} color="#7c3aed"/>
                            <span style={{ color: '#2d2640', fontSize: 12 }}>{video.titulo}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setFormVideo({ ...video, trilhaId: trilha.id, assuntoId: assunto.id })}
                              style={sc.btnIcone} title="Editar vídeo"><Edit2 size={11}/></button>
                            <button onClick={() => excluirVideo(trilha.id, assunto.id, video.id)}
                              style={{ ...sc.btnIcone, color: '#ef4444' }}><Trash2 size={11}/></button>
                          </div>
                        </div>
                      ))}

                      {/* Form inline de vídeo */}
                      {formVideo?.assuntoId === assunto.id && formVideo?.trilhaId === trilha.id && (
                        <div style={{ background: '#ffffff', border: '1px solid rgba(124,58,237,0.4)', borderRadius: 8, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <span style={{ color: '#7c3aed', fontSize: 12, fontWeight: 700 }}>{formVideo.id ? 'Editar Vídeo' : '＋ Novo Vídeo'}</span>
                          <input style={sc.input} placeholder="Título do vídeo *" value={formVideo.titulo} onChange={e => setFormVideo({ ...formVideo, titulo: e.target.value })}/>
                          <input style={sc.input} placeholder="URL do YouTube (ex: https://youtube.com/watch?v=...) *" value={formVideo.url} onChange={e => setFormVideo({ ...formVideo, url: e.target.value })}/>
                          {formVideo.url && getYoutubeId(formVideo.url) === null && (
                            <span style={{ color: '#f59e0b', fontSize: 11 }}>⚠️ URL inválida — use um link do YouTube</span>
                          )}
                          <input style={sc.input} placeholder="Descrição (opcional)" value={formVideo.descricao || ''} onChange={e => setFormVideo({ ...formVideo, descricao: e.target.value })}/>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={salvarVideo} style={sc.btnSalvar}>Salvar</button>
                            <button onClick={() => setFormVideo(null)} style={sc.btnCancelar}>Cancelar</button>
                          </div>
                        </div>
                      )}

                      {/* Botão adicionar vídeo */}
                      {!(formVideo?.assuntoId === assunto.id && formVideo?.trilhaId === trilha.id) && (
                        <button
                          onClick={() => setFormVideo({ titulo: '', url: '', descricao: '', trilhaId: trilha.id, assuntoId: assunto.id })}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px dashed #e5e0ef', borderRadius: 6, color: '#7c3aed', padding: '6px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start' }}>
                          <Plus size={13}/> Adicionar Vídeo
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── ABA: RELATÓRIO (admin) ── */}
      {aba === 'relatorio' && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: '#1c1530', fontWeight: 600, fontSize: 15 }}>📊 Relatório de Visualizações</span>
            <button onClick={exportarRelatorio}
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
              ⬇ Exportar CSV
            </button>
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <select value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)} style={sc.input}>
              <option value="">Todas as empresas</option>
              {empresas.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <select value={filtroTrilha} onChange={e => setFiltroTrilha(e.target.value)} style={sc.input}>
              <option value="">Todas as trilhas</option>
              {trilhas.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
            <button onClick={carregarVisualizacoes} style={{ ...sc.btnCancelar, color: '#7c3aed', borderColor: '#7c3aed' }}>🔄 Atualizar</button>
          </div>

          {relatorio.length === 0
            ? <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>Nenhuma visualização encontrada.</p>
            : relatorio.map((r, i) => (
              <div key={i} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '0.75rem 1rem', background: '#f7f5fb', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Eye size={14} color="#7c3aed"/>
                  <span style={{ color: '#1c1530', fontWeight: 700, fontSize: 13 }}>{r.nome}</span>
                  {r.empresa && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)' }}>🏢 {r.empresa}</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>{r.videos.length} vídeo(s) assistido(s)</span>
                </div>
                <div style={{ padding: '0.5rem 1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Trilha','Assunto','Vídeo','Data'].map(h => <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #eeeaf5' }}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {r.videos.map((v, j) => (
                        <tr key={j}>
                          <td style={{ padding: '6px 8px', fontSize: 12, color: '#2d2640', borderBottom: '1px solid #e5e0ef' }}>{v.trilha}</td>
                          <td style={{ padding: '6px 8px', fontSize: 12, color: '#2d2640', borderBottom: '1px solid #e5e0ef' }}>{v.assunto}</td>
                          <td style={{ padding: '6px 8px', fontSize: 12, color: '#2d2640', borderBottom: '1px solid #e5e0ef' }}>{v.video}</td>
                          <td style={{ padding: '6px 8px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #e5e0ef', whiteSpace: 'nowrap' }}>{v.data ? new Date(v.data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

const sc = {
  card:       { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  cardTitulo: { fontSize: 13, fontWeight: 700, color: '#1c1530' },
  input:      { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, color: '#1c1530', padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' },
  btnSalvar:  { background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnCancelar:{ background: 'transparent', color: '#5f5874', border: '1px solid #e5e0ef', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  btnIcone:   { background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' },
  btnX:       { background: 'none', border: 'none', color: '#5f5874', cursor: 'pointer', display: 'flex' },
};
